import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { ConfirmDialog } from '@/components/ui/dialog';
import { isUnmatchedRefusal, useAcceptImport, useDiscardImport, usePendingImport } from '@/hooks/useImports';
import { cn } from '@/lib/utils';
import { plural, sourceLabel } from './ReviewImport';

const linkClass =
  'font-medium underline-offset-4 hover:underline disabled:opacity-50 disabled:pointer-events-none';

/**
 * The amber bar above the timesheet (ON15) while an import waits for its OK.
 * Review opens the sheet via `?review=<id>`; Accept all and Discard act here.
 */
export function PendingImportBar() {
  const pending = usePendingImport();
  const accept = useAcceptImport();
  const discard = useDiscardImport();
  const [, setParams] = useSearchParams();
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  const batch = pending.data?.batch;
  if (!batch) return null;

  const entries = batch.summary?.counts?.entry ?? 0;
  const busy = accept.isPending || discard.isPending;

  const review = () =>
    setParams((prev) => {
      prev.set('review', batch.id);
      return prev;
    });

  return (
    <>
      <div
        role="status"
        className={cn(
          'flex flex-wrap items-center gap-x-4 gap-y-1 rounded-xl border px-4 py-2.5 text-sm',
          'border-[#fde68a] bg-[#fffbeb] text-[#92400e]',
          'dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-300'
        )}
      >
        <span className="min-w-0 flex-1">
          {plural(entries, 'entry', 'entries')} from {sourceLabel(batch.source)} are waiting for
          your OK
        </span>
        <div className="flex items-center gap-4">
          <button type="button" className={linkClass} onClick={review} disabled={busy}>
            Review
          </button>
          <button
            type="button"
            className={linkClass}
            onClick={() =>
              accept.mutate(
                { id: batch.id, mode: 'all' },
                // Some clients still need a match: take them to where that happens.
                { onError: (error) => isUnmatchedRefusal(error) && review() }
              )
            }
            disabled={busy}
          >
            {accept.isPending ? 'Accepting…' : 'Accept all'}
          </button>
          <button
            type="button"
            className={linkClass}
            onClick={() => setConfirmDiscard(true)}
            disabled={busy}
          >
            Discard
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={confirmDiscard}
        onOpenChange={setConfirmDiscard}
        title="Discard this import?"
        description={`${plural(entries, 'entry', 'entries')} from ${sourceLabel(batch.source)} will be thrown away. Nothing was added to your books.`}
        confirmLabel="Discard"
        destructive
        onConfirm={() => discard.mutate(batch.id)}
      />
    </>
  );
}
